/**
 * Fleet API client for the SignalSentry client app.
 *
 * A typed fetch wrapper that:
 * - Reads the fleet API base URL from the env module
 * - Automatically adds Bearer token from localStorage via authStorage
 * - On 401: attempts a silent token refresh (rotation-on-use); retries the
 *   original request with the new access token; if refresh also fails,
 *   dispatches 'signalsentry:auth:expired' and clears local tokens
 * - Throws ApiError for non-2xx responses with structured error shape
 *
 * Usage:
 *   import { api } from '@/lib/api-client';
 *   const user = await api.get<UserResponse>('/v1/auth/me');
 *
 * Circular dependency note:
 *   refreshRequest is imported via dynamic import() to break the circular
 *   reference (api.ts imports authStorage and env; api-client imports api.ts).
 *   The dynamic import is evaluated lazily on the first 401 hit.
 */
import { env } from './env';
import { authStorage, staffSidecar, IMPERSONATION_NO_REFRESH } from './auth-storage';

/** Storage keys for auth tokens in localStorage. */
export const TOKEN_KEYS = {
  access: 'signalsentry:access_token',
  refresh: 'signalsentry:refresh_token',
} as const;

/** Shape of API error responses from fleet-api. */
export interface ApiErrorShape {
  detail: string;
  code?: string;
}

/**
 * Structured error class for non-2xx API responses.
 * Carries the HTTP status code and parsed error body.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiErrorShape,
  ) {
    super(body.detail);
    this.name = 'ApiError';
  }
}

/** Options for apiFetch - extends RequestInit with typed body. */
type FetchOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
  signal?: AbortSignal;
};

/**
 * Module-level refresh lock.
 *
 * When multiple concurrent requests hit 401 simultaneously, only the FIRST
 * one fires a POST /v1/auth/refresh. All others await the same shared Promise
 * so exactly one refresh is in-flight at any time.
 *
 * The lock is cleared in the finally block after the refresh settles
 * (success or failure) so the next 401 cycle starts fresh.
 */
let refreshPromise: Promise<string> | null = null;

/**
 * Attempts to refresh the access token using the stored refresh token.
 *
 * Returns the new access token string on success.
 * Throws on failure (invalid refresh token, network error, etc.).
 *
 * Race safety: if a refresh is already in-flight, the existing Promise is
 * returned directly so only one POST /v1/auth/refresh fires.
 */
async function refreshAccessToken(): Promise<string> {
  // Return existing promise if a refresh is already in-flight.
  if (refreshPromise) return refreshPromise;

  const refresh = authStorage.getRefresh();
  if (!refresh) {
    // No refresh token - cannot refresh; signal session expiry immediately.
    throw new ApiError(401, { detail: 'no_refresh_token' });
  }

  refreshPromise = (async () => {
    try {
      // Dynamic import breaks the circular dependency between api-client and auth/api.
      const { refreshRequest } = await import('@/features/auth/api');
      const res = await refreshRequest(refresh);
      authStorage.set(res.access_token, res.refresh_token);
      return res.access_token;
    } finally {
      // Clear the shared lock after the promise settles so the next 401 cycle
      // can start a new refresh.
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Shared silent-refresh entry point for the non-apiFetch callers (the live
 * site-data poller's `authedFetch` and the WebSocket reconnect). These paths
 * historically bypassed the 401-refresh-retry that apiFetch has, so once the
 * 15-minute access token expired they failed (proxy 401 cascade; WS 1008
 * reconnect loop on a stale token) instead of recovering. This helper lets
 * them reuse the SAME de-duped refresh so all three surfaces share one
 * in-flight POST /v1/auth/refresh.
 *
 * Returns the new access token on success, or `null` when a refresh is not
 * possible / not appropriate:
 *   - active impersonation session (refresh slot holds the sentinel, which is
 *     not a JWT; its expiry is driven by apiFetch's graceful exit path, not a
 *     /v1/auth/refresh call), or
 *   - no refresh token / the refresh request itself failed (genuinely dead
 *     session). Callers should then fall back to their existing
 *     expiry-signalling behaviour.
 *
 * Never throws - callers branch on null.
 */
export async function tryRefreshAccessToken(): Promise<string | null> {
  if (authStorage.getRefresh() === IMPERSONATION_NO_REFRESH) return null;
  try {
    return await refreshAccessToken();
  } catch {
    return null;
  }
}

/**
 * Dispatches the auth-expired custom event, clears stored tokens, AND
 * hard-redirects to /login with the current path stashed as `returnTo`
 * (2026-05-26 fix).
 *
 * The AuthProvider listens for the event to update React state, but
 * the event-listener path alone is racy in two ways:
 *
 *   1. The router rebuilds with public-only routes (the staff/client
 *      branches in build-routes.tsx deregister /admin/* once user is
 *      null). If the current URL is `/admin/fleet`, React Router has
 *      no match -> falls through to the catch-all `path='*'` ->
 *      <NotFoundPage /> renders OUTSIDE the AppShell layout, so the
 *      operator sees a bare 404 with no navbar and has to manually
 *      navigate back home.
 *   2. If the current URL is /now (the only route still registered
 *      under publicRoutes for the impersonating-staff branch), the
 *      operator lands on the client-facing NowPage WITHOUT the admin
 *      tabs they normally see — the navbar is there but the staff
 *      chrome is gone because NowPage is a client page.
 *
 * The hard redirect solves both: regardless of where the user was,
 * they always end up on /login with a returnTo query param. After
 * successful login, LoginPage honors returnTo and bounces back.
 *
 * window.location.replace is intentional (NOT a React Router push):
 * the React Router state is about to be invalidated by the AuthProvider
 * setting user=null, so we want a full DOM reset, not a SPA navigation
 * that races the router rebuild.
 *
 * Skip the redirect when:
 *   - Already on /login (avoid infinite redirect loop on a fresh
 *     login attempt that 401s).
 *   - SSR / non-browser context (window undefined).
 */
function signalSessionExpired(): void {
  authStorage.clear();
  window.dispatchEvent(new CustomEvent('signalsentry:auth:expired'));
  redirectToLogin();
}

/**
 * Hard-redirect the browser to /login with the current path stashed as a
 * `returnTo` query param, so a successful re-login bounces the user back to
 * where they were instead of stranding them.
 *
 * `window.location.replace` (NOT a React Router push) is intentional: the
 * React Router state is about to be invalidated (AuthProvider sets user=null
 * and the route table rebuilds), so we want a full DOM reset rather than a SPA
 * navigation that races the router rebuild.
 *
 * Exported so the AuthProvider bootstrap can reuse the exact same redirect on
 * a non-recoverable session (its /me bootstrap does not go through apiFetch).
 *
 * Guards:
 *   - No-op in a non-browser (SSR / unit-test) context (window undefined).
 *   - No-op when already on /login, so a failed login attempt's 401 cannot
 *     loop the page.
 */
export function redirectToLogin(): void {
  if (typeof window === 'undefined') return;
  // Don't redirect if we're already at /login (otherwise a failed login
  // attempt's 401 would loop the page).
  if (window.location.pathname === '/login') return;
  const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
  // `replace` so the back button doesn't take the user back to a page they'll
  // just be auth-expired off of again.
  window.location.replace(`/login?returnTo=${returnTo}`);
}

/**
 * Phase 3.1 Plan 03 (B2 mitigation, per ADR-005 1-hour cap):
 *
 * Recover gracefully when an impersonation access token expires mid-session.
 *
 * Pre-conditions: a 401 fired on a regular API call AND the active refresh
 * slot equals IMPERSONATION_NO_REFRESH (so calling /v1/auth/refresh would
 * be wrong; the sentinel is not a JWT).
 *
 * Steps:
 *   1. Best-effort POST /v1/admin/impersonate/exit to close the server-side
 *      session. The exit endpoint is idempotent per ADR-005; on cap-expiry
 *      the session may already be closed and the call returns 401, which
 *      we swallow.
 *   2. Restore staff tokens from sidecar to active slots; clear sidecar.
 *      If sidecar is absent (corruption), clear active tokens too.
 *   3. console.warn the recovery so the user has a textual signal in the
 *      devtools console. Phase 7 will swap this to a Radix toast in
 *      src/components/Toaster.tsx.
 *   4. Hard-navigate to /admin/fleet via window.location.assign. A hard
 *      reload guarantees React Query in-flight queries are cancelled and
 *      the router rebuilds with staff routes; the new /me bootstrap fires
 *      on the destination route. The user does NOT bounce to /login.
 *
 * The dynamic import of '@/features/impersonation/api' breaks the import
 * cycle (impersonation/api imports auth-storage; auth-storage is imported
 * by api-client; if api-client imported impersonation/api eagerly at the
 * top, we'd have a cycle).
 */
async function handleImpersonationAccessExpiry(): Promise<void> {
  // Step 1: best-effort server-side close. Lazy import to avoid the cycle
  // described above.
  try {
    const { exitImpersonationRequest } = await import(
      '@/features/impersonation/api'
    );
    await exitImpersonationRequest();
  } catch {
    // Best-effort. Server may already have closed the session, or the
    // dynamic import may have failed (e.g., chunk load error mid-deploy);
    // in either case we proceed with the local swap-back.
  }

  // Step 2: restore staff or clear.
  const staff = staffSidecar.get();
  if (staff) {
    authStorage.set(staff.access, staff.refresh);
    staffSidecar.clear();
  } else {
    authStorage.clear();
  }

  // Step 3: textual signal. Phase 7 will replace with a Radix toast.
  // eslint-disable-next-line no-console
  console.warn(
    'Impersonation session expired; returned to admin console',
  );

  // Step 4: hard nav. /admin/fleet is the staff landing page; the new /me
  // bootstrap on the destination will redirect to /login if the staff
  // refresh has also expired (rare; only if impersonation lasted >24h).
  window.location.assign('/admin/fleet');
}

/**
 * Core fetch wrapper for all fleet API requests.
 *
 * Handles 401 responses by attempting a single silent token refresh and
 * retrying the original request. If the refresh also fails, the session
 * is terminated (tokens cleared, auth-expired event dispatched).
 *
 * @param path - API path, e.g. '/v1/alerts'
 * @param options - Fetch options with optional typed body
 * @returns Parsed JSON response typed as T
 * @throws ApiError on non-2xx responses (after retry if applicable)
 */
export async function apiFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  /**
   * Build a fetch Request with the current access token.
   * @param token - Access token to include in Authorization header, or null
   */
  const doFetch = (token: string | null): Promise<Response> => {
    const headers = new Headers(options.headers as HeadersInit | undefined);

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    if (options.body !== undefined) {
      headers.set('Content-Type', 'application/json');
    }

    return fetch(`${env.FLEET_API_URL}${path}`, {
      ...options,
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });
  };

  // First attempt with the current access token.
  let res = await doFetch(authStorage.getAccess());

  if (res.status === 401) {
    // Phase 3.1 Plan 03 (B2): if the active refresh slot holds the
    // impersonation sentinel, this 401 is the impersonation access token
    // expiring (1-hour cap per ADR-005). Do NOT call /v1/auth/refresh
    // (the sentinel is not a JWT). Instead route to the graceful
    // exit-impersonation recovery path: server-side close, restore staff
    // from sidecar, navigate to /admin/fleet, show toast. The user does
    // NOT bounce to /login.
    const currentRefresh = authStorage.getRefresh();
    if (currentRefresh === IMPERSONATION_NO_REFRESH) {
      await handleImpersonationAccessExpiry();
      // Abort the in-flight retry chain. window.location.assign has already
      // navigated away so the caller's promise rejection is just the
      // signal that this request did not complete; React Query will see
      // the rejection but the page is already navigating to /admin/fleet.
      throw new ApiError(401, {
        detail: 'impersonation_access_expired',
        code: 'impersonation_access_expired',
      });
    }

    // Attempt a silent refresh then retry the original call.
    try {
      const newToken = await refreshAccessToken();
      res = await doFetch(newToken);
    } catch {
      // Refresh failed or no refresh token - terminate the session.
      signalSessionExpired();
      throw new ApiError(401, { detail: 'session_expired' });
    }
  }

  if (!res.ok) {
    let errorBody: ApiErrorShape;
    try {
      errorBody = (await res.json()) as ApiErrorShape;
    } catch {
      errorBody = { detail: `http_${res.status}` };
    }
    throw new ApiError(res.status, errorBody);
  }

  // 204 No Content - return undefined cast to T
  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

/**
 * Convenience API object with typed HTTP method wrappers.
 * All methods use apiFetch internally.
 */
export const api = {
  /** HTTP GET request. */
  get<T>(path: string, signal?: AbortSignal): Promise<T> {
    return apiFetch<T>(path, { method: 'GET', signal });
  },

  /** HTTP POST request with JSON body. */
  post<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    return apiFetch<T>(path, { method: 'POST', body, signal });
  },

  /** HTTP PATCH request with JSON body. */
  patch<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    return apiFetch<T>(path, { method: 'PATCH', body, signal });
  },

  /** HTTP PUT request with JSON body. */
  put<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    return apiFetch<T>(path, { method: 'PUT', body, signal });
  },

  /** HTTP DELETE request. */
  delete<T>(path: string, signal?: AbortSignal): Promise<T> {
    return apiFetch<T>(path, { method: 'DELETE', signal });
  },
} as const;
